import { Plus, Trash2 } from 'lucide-react';
import { nomeDe, type Pessoa } from '@social/shared';
import { Botao, Campo, Entrada, Selecao } from '../componentes/ui';
import { centavosDeReais, moedaBR } from './sociedade-nova-comum';
import { LinhaCheia, Secao } from './sociedade-nova-secao';

/**
 * Uma cessão em edição — texto, porque é o que está na tela.
 *
 * `valor` vazio significa cessão GRATUITA (doação ou permuta), e não "R$ 0,00":
 * escrever zero declararia um negócio oneroso de valor nulo, que é outra coisa
 * perante a Junta e perante o fisco.
 */
export interface RascunhoCessao {
  cedenteId: string;
  cessionarioId: string;
  quotas: string;
  valor: string;
}

export const CESSAO_VAZIA: RascunhoCessao = {
  cedenteId: '',
  cessionarioId: '',
  quotas: '',
  valor: '',
};

/**
 * A declaração de CESSÃO de quotas.
 *
 * ## Por que ela precisa ser declarada, e não deduzida
 * O servidor compara os dois estados da sociedade e descobre sozinho quase
 * tudo — endereço, capital, objeto, quem entrou, quem saiu. A cessão é a
 * exceção: "o sócio A perdeu 500 quotas e o B ganhou 500" tanto é cessão
 * (quota mudando de dono, com negócio entre as partes, anuência dos demais
 * sócios pelo art. 1.057 do Código Civil, e tributação) quanto redistribuição
 * sem cessão nenhuma. A diferença é a INTENÇÃO, e ela não está nos números.
 *
 * Por isso este formulário existe: é onde a intenção entra. Sem ele, a mesma
 * movimentação sairia no documento como "alteração da participação
 * societária" — sem a cláusula de anuência que a cessão exige.
 *
 * ## O que ele NÃO faz
 * Não mexe nas quotas. Quem move as quotas é a seção de sócios, acima; aqui
 * só se declara o que aquela movimentação FOI. Fazer os dois no mesmo lugar
 * pareceria mais prático e criaria duas fontes para o mesmo número — e no dia
 * em que divergissem, o documento sairia dizendo uma coisa e o quadro de
 * quotas mostrando outra.
 */
export function SecaoCessoes({
  cessoes,
  aoMudar,
  pessoas,
  sociosAtuais,
}: {
  cessoes: RascunhoCessao[];
  aoMudar: (c: RascunhoCessao[]) => void;
  pessoas: Pessoa[];
  /** Ids dos sócios que estão na sociedade — só eles podem ceder. */
  sociosAtuais: string[];
}): JSX.Element {
  const mexer = (i: number, parcial: Partial<RascunhoCessao>) =>
    aoMudar(cessoes.map((c, j) => (j === i ? { ...c, ...parcial } : c)));

  // Cedente só entre quem JÁ é sócio: quem não tem quota não cede quota, e
  // oferecer a lista inteira convidaria a um ato que a Junta devolve.
  const podemCeder = pessoas.filter((p) => sociosAtuais.includes(p.id));

  return (
    <Secao
      titulo="Cessão de quotas"
      descricao="Só quando quotas mudam de DONO. Movimente as quotas na seção de sócios e declare a cessão aqui — é ela que traz a anuência dos demais sócios ao documento."
    >
      <LinhaCheia>
        <div className="flex flex-col gap-3">
          {cessoes.length === 0 && (
            <p className="text-tinta-500 text-sm">
              Nenhuma cessão declarada. Redistribuição de quotas entre os sócios sem
              negócio entre eles não é cessão — deixe vazio.
            </p>
          )}

          {cessoes.map((c, i) => (
            <div
              key={i}
              className="border-tinta-100 grid grid-cols-1 items-end gap-3 rounded-lg border p-3 sm:grid-cols-[2fr_2fr_1fr_1fr_auto]"
            >
              <Campo rotulo="Cedente" id={`cedente-${String(i)}`} obrigatorio>
                <Selecao
                  id={`cedente-${String(i)}`}
                  value={c.cedenteId}
                  onChange={(e) => mexer(i, { cedenteId: e.target.value })}
                >
                  <option value="">Selecione…</option>
                  {podemCeder.map((p) => (
                    <option key={p.id} value={p.id}>
                      {nomeDe(p)}
                    </option>
                  ))}
                </Selecao>
              </Campo>

              <Campo rotulo="Cessionário" id={`cessionario-${String(i)}`} obrigatorio>
                <Selecao
                  id={`cessionario-${String(i)}`}
                  value={c.cessionarioId}
                  onChange={(e) => mexer(i, { cessionarioId: e.target.value })}
                >
                  <option value="">Selecione…</option>
                  {/*
                    Aqui a lista é INTEIRA, e não só os sócios: a cessão é
                    justamente o caminho por onde alguém de fora entra na
                    sociedade. Limitar aos sócios atuais impediria o caso mais
                    comum de todos.
                  */}
                  {pessoas
                    .filter((p) => p.id !== c.cedenteId)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {nomeDe(p)}
                      </option>
                    ))}
                </Selecao>
              </Campo>

              <Campo
                rotulo="Quotas cedidas"
                id={`cessao-quotas-${String(i)}`}
                obrigatorio
              >
                <Entrada
                  id={`cessao-quotas-${String(i)}`}
                  inputMode="numeric"
                  value={c.quotas}
                  onChange={(e) => mexer(i, { quotas: e.target.value })}
                />
              </Campo>

              {/*
                "Quotas cedidas" e "Preço da cessão", e não "Quotas" e "Valor":
                a seção de sócios logo acima já tem um campo "Quotas" e um
                "Valor nominal da quota". Rótulo repetido na mesma tela deixa
                o leitor de tela anunciar dois campos indistinguíveis — e o
                teste, que localiza por rótulo, achar dois elementos.
              */}
              <Campo
                rotulo="Preço da cessão (R$)"
                id={`cessao-valor-${String(i)}`}
                dica="Vazio = gratuita"
              >
                <Entrada
                  id={`cessao-valor-${String(i)}`}
                  inputMode="decimal"
                  value={c.valor}
                  onChange={(e) => mexer(i, { valor: e.target.value })}
                />
              </Campo>

              <Botao
                variante="fantasma"
                tamanho="icone"
                aria-label={`Remover cessão ${String(i + 1)}`}
                title="Remover esta cessão"
                onClick={() => aoMudar(cessoes.filter((_, j) => j !== i))}
              >
                <Trash2 className="size-4" aria-hidden />
              </Botao>
            </div>
          ))}

          <div className="flex items-center gap-3">
            <Botao
              variante="secundaria"
              onClick={() => aoMudar([...cessoes, CESSAO_VAZIA])}
            >
              <Plus className="size-4" aria-hidden />
              Declarar cessão
            </Botao>

            {cessoes.length > 0 && (
              <span className="text-tinta-500 text-sm tabular-nums">
                Total cedido: {totalDeQuotas(cessoes).toLocaleString('pt-BR')} quotas ·{' '}
                {moedaBR(totalEmCentavos(cessoes))}
              </span>
            )}
          </div>
        </div>
      </LinhaCheia>
    </Secao>
  );
}

function totalDeQuotas(cessoes: RascunhoCessao[]): number {
  return cessoes.reduce((t, c) => t + (Number(c.quotas) || 0), 0);
}

function totalEmCentavos(cessoes: RascunhoCessao[]): number {
  return cessoes.reduce((t, c) => t + centavosDeReais(c.valor), 0);
}

/**
 * As cessões da tela viram o que a API espera — descartando as incompletas.
 *
 * Linha em branco é gesto normal: a pessoa clica "Declarar cessão" e ainda não
 * escolheu ninguém. Mandá-la ao servidor produziria um 422 no meio do trabalho
 * por causa de uma linha que ela nem começou a preencher.
 */
export function cessoesParaDto(cessoes: RascunhoCessao[]) {
  return cessoes
    .filter((c) => c.cedenteId && c.cessionarioId && Number(c.quotas) > 0)
    .map((c) => ({
      cedenteId: c.cedenteId,
      cessionarioId: c.cessionarioId,
      quotas: Number(c.quotas),
      // Vazio é `null` (gratuita), nunca 0 — ver `RascunhoCessao`.
      valorCentavos: c.valor.trim() === '' ? null : centavosDeReais(c.valor),
    }));
}
